import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSound } from "./SoundContext";

// Brief, branded route-transition overlay: a stone-gray panel slides over,
// the infinity loop draws itself in mint, then the panel lifts to reveal the new page.
// First page load also plays a quick "draw-in" hero loader.

export default function RouteTransition() {
  const location = useLocation();
  const [phase, setPhase] = useState("hidden"); // hidden | covering | drawing | uncovering
  const [firstLoad, setFirstLoad] = useState(true);
  const { playWhoosh } = useSound();

  useEffect(() => {
    // Skip the very first mount to avoid covering the initial hero animation
    if (firstLoad) {
      setFirstLoad(false);
      // brief opening animation only
      setPhase("opening");
      const t = setTimeout(() => setPhase("hidden"), 1600);
      return () => clearTimeout(t);
    }

    // Route changed -> play the full transition + whoosh sound
    playWhoosh();
    setPhase("covering");
    const t1 = setTimeout(() => setPhase("drawing"), 360);
    const t2 = setTimeout(() => setPhase("uncovering"), 1450);
    const t3 = setTimeout(() => setPhase("hidden"), 2000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (phase === "hidden") return null;

  // panel transform per phase
  let panelStyle = {};
  if (phase === "opening") {
    // start covering and slide up away immediately (lets initial hero show through)
    panelStyle = { transform: "translateY(-100%)", transition: "transform 900ms cubic-bezier(0.76, 0, 0.24, 1) 700ms" };
  } else if (phase === "covering") {
    panelStyle = { transform: "translateY(0)", transition: "transform 360ms cubic-bezier(0.76, 0, 0.24, 1)" };
  } else if (phase === "drawing") {
    panelStyle = { transform: "translateY(0)" };
  } else if (phase === "uncovering") {
    panelStyle = { transform: "translateY(-100%)", transition: "transform 550ms cubic-bezier(0.76, 0, 0.24, 1)" };
  }

  // Initial mount: start the panel covering the screen so opening animation is visible
  if (phase === "opening" && !panelStyle.transform) {
    panelStyle = { transform: "translateY(0)" };
  }

  const showDraw = phase === "drawing" || phase === "uncovering" || phase === "opening";

  return (
    <div
      data-testid="route-transition"
      aria-hidden="true"
      className="fixed inset-0 z-[100] pointer-events-none"
    >
      <div
        className="absolute inset-0 bg-stone flex items-center justify-center"
        style={panelStyle}
      >
        <svg
          viewBox="0 0 600 200"
          className="w-[70%] max-w-2xl h-auto"
          fill="none"
          stroke="#C2FFE1"
          strokeWidth={5}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path
            d="M10,150 Q40,160 90,150 Q140,140 180,120 Q230,90 220,60 Q210,30 170,30 Q120,30 130,80 Q140,130 200,150 Q260,170 320,150 Q380,130 400,100 Q420,70 410,50 Q400,30 360,40 Q320,55 340,100 Q360,140 420,150 Q480,160 540,140 Q590,125 595,110"
            style={{
              strokeDasharray: 2200,
              strokeDashoffset: showDraw ? 0 : 2200,
              transition: showDraw
                ? "stroke-dashoffset 1100ms cubic-bezier(0.65, 0, 0.35, 1)"
                : "none",
            }}
          />
        </svg>
        <p
          className="absolute bottom-12 text-mint/80 text-[10px] uppercase tracking-[0.5em] font-body font-medium"
          style={{
            opacity: showDraw ? 1 : 0,
            transition: "opacity 600ms ease 500ms",
          }}
        >
          unite
        </p>
      </div>
    </div>
  );
}
